import React from "react";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { TFunction } from "i18next";
import { DefinitionTooltip } from "src/components/common";
import type {
  NotificationChannel,
  NotificationSettingsItem,
  NotificationSystem,
} from "../Types/NotificationTypes";
import { getNotificationMetricHelp } from "../Utils/NotificationMetricHelp";
import { getRuleChipLabel } from "../Utils/NotificationRuleHelpers";
import NotificationToggleCell from "./NotificationToggleCell";
import "../Styles/NotificationActivityRow.scss";

interface ActivityRowProps {
  item: NotificationSettingsItem;
  system: NotificationSystem;
  sectionIdx: number;
  itemIdx: number;
  isConfigOpen: boolean;
  deletingRuleId: number | null;
  t: TFunction;
  language: string;
  onToggle: (_s: number, _i: number, _channel: NotificationChannel, _checked: boolean) => void;
  onConfigure: (_s: number, _i: number) => void;
  onDelete: (_s: number, _i: number) => void;
}

const NotificationActivityRow: React.FC<ActivityRowProps> = ({
  item,
  system,
  sectionIdx,
  itemIdx,
  isConfigOpen,
  deletingRuleId,
  t,
  language,
  onToggle,
  onConfigure,
  onDelete,
}) => {
  const ruleId = item.rule ? item.rule.rule_id : null;
  const isDeleting = ruleId !== null && deletingRuleId === ruleId;
  const isChannelActive = Boolean(
    item.rule && (item.rule.email || item.rule.push || item.rule.in_app)
  );
  const metricHelp = getNotificationMetricHelp(t, system, item.key, item.label);

  return (
    <tr className={isConfigOpen ? "activity-row-active" : ""}>
      <td className="activity-details">
        <span className="activity-name-row">
          <span className="activity-name">{item.label}</span>
          {metricHelp ? (
            <DefinitionTooltip
              label={item.label}
              info={metricHelp}
              formulaTitle={t("notificationPreferences.formulaLabel", {
                defaultValue: "Formula",
              })}
            />
          ) : null}
        </span>
      </td>
      <NotificationToggleCell
        configured={item.configured}
        checked={item.rule?.email ?? false}
        ariaLabel={t("notificationPreferences.table.emailToggle", {
          label: item.label,
          defaultValue: `Email notifications for ${item.label}`,
        })}
        onChange={checked => onToggle(sectionIdx, itemIdx, "email", checked)}
      />
      <NotificationToggleCell
        configured={item.configured}
        checked={item.rule?.push ?? false}
        ariaLabel={t("notificationPreferences.table.pushToggle", {
          label: item.label,
          defaultValue: `Push notifications for ${item.label}`,
        })}
        onChange={checked => onToggle(sectionIdx, itemIdx, "push", checked)}
      />
      <NotificationToggleCell
        configured={item.configured}
        checked={item.rule?.in_app ?? false}
        ariaLabel={t("notificationPreferences.table.inAppToggle", {
          label: item.label,
          defaultValue: `In-app notifications for ${item.label}`,
        })}
        onChange={checked => onToggle(sectionIdx, itemIdx, "in_app", checked)}
      />
      <td className="threshold-cell">
        {item.configured && item.rule ? (
          <div className="threshold-actions-cell">
            <button
              className={`threshold-badge${isChannelActive ? "" : " threshold-badge--muted"}`}
              onClick={() => onConfigure(sectionIdx, itemIdx)}
            >
              <span>{getRuleChipLabel(item, t, language)}</span>
              <EditOutlined className="threshold-edit-icon" />
            </button>
            <button
              className={`threshold-delete-btn${isChannelActive ? "" : " threshold-delete-btn--muted"}`}
              onClick={() => onDelete(sectionIdx, itemIdx)}
              disabled={isDeleting}
              title={t("notificationPreferences.table.deleteRule", {
                defaultValue: "Remove rule",
              })}
            >
              <DeleteOutlined />
            </button>
          </div>
        ) : (
          <button
            className={`configure-btn button button--outline${isConfigOpen ? " configure-btn--active" : ""}`}
            onClick={() => onConfigure(sectionIdx, itemIdx)}
          >
            {t("notificationPreferences.table.configure", {
              defaultValue: "Configure",
            })}
          </button>
        )}
      </td>
    </tr>
  );
};

export default NotificationActivityRow;
